import { useForm } from '@tanstack/react-form';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  CreditCard,
  Eye,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  type TimelineEvent,
  deriveVendorTTStatus,
  getVendorTTRejectionReason,
} from '@cold-ambulance/isomorphic';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Textarea,
  buttonVariants,
  cn,
} from '@cold-ambulance/shadcn';
import {
  ContractPageSkeleton,
  FieldMessage,
  FormMessage,
  SignatureStatusCard,
  SigningEmailNotice,
  ViewStoredFile,
  errorToFormIssue,
  formatDate,
  useAction,
  useData,
} from '@cold-ambulance/ui';

export default function OfferContractPage() {
  const { t } = useTranslation();

  return (
    <div className="page-container">
      <div className="mx-auto w-full max-w-2xl">
        <Link
          to="/offers"
          className="text-primary mb-6 flex items-center gap-1 text-sm font-bold"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('offers.backToOffers')}</span>
        </Link>

        <h1 className="mb-8 text-3xl font-bold">
          {t('contract.contractStatus')}
        </h1>

        <ContractStatusBody />
      </div>
    </div>
  );
}

function ContractStatusBody() {
  const { t } = useTranslation();
  const { offerId } = useParams();

  const contractStatusQuery = useData(
    'GET /offers/{id}/contract/status',
    { id: offerId! },
    { enabled: !!offerId },
  );

  if (contractStatusQuery.isLoading) {
    return <ContractPageSkeleton />;
  }

  if (contractStatusQuery.isError) {
    const errorMessage =
      contractStatusQuery.error instanceof Error
        ? contractStatusQuery.error.message
        : t('documents.errorLoading');

    return (
      <>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
        <Link
          to="/offers"
          className={buttonVariants({
            variant: 'outline',
            className: 'mt-4',
          })}
        >
          {t('offers.backToOffers')}
        </Link>
      </>
    );
  }

  const data = contractStatusQuery.data;
  if (!data) return null;

  const { vendorSigned, customerSigned, bothSigned, ttDeadline } = data;

  return (
    <>
      <SignatureStatusCard
        firstSigned={vendorSigned}
        secondSigned={customerSigned}
        firstLabel={t('contract.vendorSignature')}
        secondLabel={t('contract.customerSignature')}
      />
      {bothSigned && <BothSignedNotice ttDeadline={ttDeadline} />}
      {!vendorSigned && <SigningEmailNotice />}
      <VendorTTApprovalSection offerId={offerId!} />
      {vendorSigned && !customerSigned && <AwaitingCustomerNotice />}
    </>
  );
}

function BothSignedNotice({ ttDeadline }: { ttDeadline: string | null }) {
  const { t } = useTranslation();

  return (
    <Card className="mb-6 border-green-200 bg-green-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
          <div>
            <p className="font-medium text-green-800">
              {t('contract.contractSigned')}
            </p>
            <p className="mt-1 text-sm text-green-700">
              {t('contract.contractSignedMessage')}
            </p>
            {ttDeadline && (
              <p className="mt-2 text-sm text-green-700">
                {t('contract.awaitPayment')}{' '}
                <span className="font-medium">{formatDate(ttDeadline)}</span>
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AwaitingCustomerNotice() {
  const { t } = useTranslation();

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">
              {t('contract.awaitingCustomerSignature')}
            </p>
            <p className="mt-1 text-sm text-amber-700">
              {t('contract.awaitingCustomerMessage')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const vendorTTRejectSchema = z.object({
  reason: z.string().min(1, 'سبب الرفض مطلوب'),
});

function VendorTTApprovalSection({ offerId }: { offerId: string }) {
  const { t } = useTranslation();
  const contractStatusQuery = useData('GET /offers/{id}/contract/status', {
    id: offerId,
  });

  if (!contractStatusQuery.isSuccess) {
    return null;
  }

  const { vendorTtFileId, timeline: timelineRaw } = contractStatusQuery.data;
  if (!vendorTtFileId) {
    return null;
  }

  const timeline = timelineRaw as unknown as TimelineEvent[];
  const vendorTTStatus = deriveVendorTTStatus(timeline);

  if (vendorTTStatus === 'NOT_UPLOADED') {
    return null;
  }

  const statusConfig = {
    PENDING: {
      label: t('documents.underReview'),
      badgeBg: 'bg-amber-100',
      badgeText: 'text-amber-700',
      icon: Clock,
    },
    APPROVED: {
      label: t('documents.approved'),
      badgeBg: 'bg-green-100',
      badgeText: 'text-green-700',
      icon: CheckCircle,
    },
    REJECTED: {
      label: t('documents.rejected'),
      badgeBg: 'bg-red-100',
      badgeText: 'text-red-700',
      icon: XCircle,
    },
  } as const;

  const config = statusConfig[vendorTTStatus as keyof typeof statusConfig];
  if (!config) return null;

  const StatusIcon = config.icon;
  const canAct = vendorTTStatus === 'PENDING' || vendorTTStatus === 'REJECTED';
  const rejectionReason = getVendorTTRejectionReason(timeline);

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5" />
          {t('contract.vendorTransfer')}
        </CardTitle>
        <CardDescription className="flex items-center gap-2">
          <span>{t('contract.statusLabel')}</span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              config.badgeBg,
              config.badgeText,
            )}
          >
            <StatusIcon className="h-3 w-3" />
            {config.label}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {vendorTTStatus === 'REJECTED' && rejectionReason && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('documents.rejectionReason')}</AlertTitle>
            <AlertDescription>{rejectionReason}</AlertDescription>
          </Alert>
        )}

        <ViewStoredFile
          fileId={vendorTtFileId}
          icon={<Eye className="h-4 w-4" />}
          label={t('contract.viewTransfer')}
          size="default"
          className="w-full"
        />

        {canAct && <ApproveRejectControls offerId={offerId} />}

        {vendorTTStatus === 'APPROVED' && <ApprovedNotice />}
      </CardContent>
    </Card>
  );
}

function ApproveRejectControls({ offerId }: { offerId: string }) {
  const { t } = useTranslation();
  const [showRejectForm, setShowRejectForm] = useState(false);

  const approveAction = useAction('POST /offers/{id}/vendor-tt/approve', {
    invalidate: ['GET /offers/{id}/contract/status'],
    meta: { toast: true },
    onSuccess: () => toast.success(t('shared.actions.offer.vendorTtApproved')),
  });

  if (showRejectForm) {
    return (
      <RejectForm
        offerId={offerId}
        onCancel={() => setShowRejectForm(false)}
        onRejected={() => setShowRejectForm(false)}
      />
    );
  }

  return (
    <div className="flex gap-3">
      <Button
        className="flex-1"
        onClick={() => approveAction.mutate({ id: offerId })}
        disabled={approveAction.isPending}
      >
        {approveAction.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('contract.approving')}
          </>
        ) : (
          <>
            <CheckCircle className="h-4 w-4" />
            {t('contract.approve')}
          </>
        )}
      </Button>
      <Button
        variant="destructive"
        className="flex-1"
        onClick={() => setShowRejectForm(true)}
      >
        <XCircle className="h-4 w-4" />
        {t('contract.reject')}
      </Button>
    </div>
  );
}

function RejectForm({
  offerId,
  onCancel,
  onRejected,
}: {
  offerId: string;
  onCancel: () => void;
  onRejected: () => void;
}) {
  const { t } = useTranslation();

  const rejectAction = useAction('POST /offers/{id}/vendor-tt/reject', {
    invalidate: ['GET /offers/{id}/contract/status'],
    onSuccess: () => toast.success(t('shared.actions.offer.vendorTtRejected')),
  });

  const form = useForm({
    defaultValues: { reason: '' },
    validators: {
      onSubmit: vendorTTRejectSchema,
      onSubmitAsync: async (props) => {
        try {
          await rejectAction.mutateAsync({
            id: offerId,
            reason: props.value.reason,
          });
          onRejected();
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <form
      className="space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await form.handleSubmit();
      }}
    >
      <form.Field
        name="reason"
        children={(field) => (
          <div className="space-y-2">
            <Label>{t('documents.rejectionReason')}</Label>
            <Textarea
              placeholder={t('placeholders.writeRejectionReason')}
              className="min-h-20"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />

      <div className="flex gap-3">
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              variant="destructive"
              className="flex-1"
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('contract.submitting')}
                </>
              ) : (
                t('contract.submitRejection')
              )}
            </Button>
          )}
        />
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

function ApprovedNotice() {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2 rounded-md bg-green-50 p-3">
      <CheckCircle className="h-5 w-5 text-green-600" />
      <span className="text-sm font-medium text-green-800">
        {t('contract.transferApproved')}
      </span>
    </div>
  );
}

